import React, { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import SlimeSpinsDesk, {
    DeskRibbon,
    DeskTitle,
    DeskNote,
    DeskPanel,
} from '../../../components/staff/slimespins/SlimeSpinsDesk';
import {
    getSlotAdminOverview, getSlotAdminPlayer, getSlotAdminSettings, updateSlotAdminSettings,
    getSlotWheelItems, createSlotWheelItem, updateSlotWheelItem, deleteSlotWheelItem,
    getSlotWheelLog, grantSlotWheelSpin, getSlotGifts, createSlotGift, revokeSlotGift,
    getSlotWheelDeliveries, updateSlotWheelDelivery,
} from '../../../services/api';
import { useCosmeticCatalogues, CosmeticPicker, FurniPicker } from '../../../components/staff/CosmeticPicker';
import { fetchAdminSets, fetchAdminSet } from '../../../data/frankopop';

const ITEM_CLASSES = [
    { value: 'framps', label: 'Framps' },
    { value: 'badge', label: 'Achievement' },
    { value: 'circle_bg', label: 'PFP Background' },
    { value: 'profile_skin', label: 'Profile Skin' },
    { value: 'inventory_item', label: 'Inventory Item' },
    { value: 'furni', label: 'Furni (staff delivered)' },
    { value: 'credits', label: 'Credits (staff delivered)' },
    { value: 'manual', label: 'Other prize (staff delivered)' },
    { value: 'frankopop', label: 'FrankoPop box' },
    { value: 'nothing', label: 'Blank segment' },
];

const COSMETIC_CLASSES = {
    badge: 'badgeId',
    circle_bg: 'circleBgId',
    profile_skin: 'skinId',
};

const MANUAL_CLASSES = ['furni', 'credits', 'manual'];

const SEGMENT_PALETTE = [
    '#6df35e', '#ffb340', '#e8e34a', '#3fbf43', '#ff8a3d',
    '#c6f24f', '#2f9e4f', '#ffd24a', '#8fd94a', '#d97b2b',
    '#4cb63a', '#f2c14e', '#a4e05c', '#b5651d', '#7ac943',
];

const nextSegmentColour = (items) => {
    const used = new Set(items.map(i => String(i.segmentColor || '').toLowerCase()));
    return SEGMENT_PALETTE.find(c => !used.has(c.toLowerCase()))
        || SEGMENT_PALETTE[items.length % SEGMENT_PALETTE.length];
};

const BLANK_ITEM = {
    label: '',
    description: '',
    itemClass: 'framps',
    classData: '',
    prizeQuantity: 1,
    frampAmount: 25,
    fallbackStamps: 0,
    weight: 10,
    segmentColor: '#6df35e',
    image: '',
    globalStock: '',
    isActive: true,
    sortOrder: 0,
};

const TABS = [
    { key: 'overview', label: 'Overview' },
    { key: 'wheel', label: 'Wheel Segments' },
    { key: 'deliveries', label: 'Deliveries' },
    { key: 'gifts', label: 'Gifting' },
    { key: 'settings', label: 'Settings' },
    { key: 'log', label: 'Wheel Log' },
];

const DELIVERY_KINDS = { furni: 'Furni', credits: 'Credits', manual: 'Prize' };

const parseClassData = (raw) => {
    if (!raw) return {};
    if (typeof raw === 'object') return raw;
    try { return JSON.parse(raw); } catch { return {}; }
};

const withClassData = (editing, patch) => ({
    ...editing,
    classData: JSON.stringify({ ...parseClassData(editing.classData), ...patch }),
});

const fmt = (n) => Number(n || 0).toLocaleString();
const pct = (n) => (n === null || n === undefined ? '-' : `${(n * 100).toFixed(1)}%`);
const when = (d) => (d ? new Date(d).toLocaleString() : '-');

const isLive = (item) => item.isActive && item.weight > 0 && !item.soldOut;

const oneIn = (chance) => (chance > 0 ? `1 in ${Math.round(1 / chance).toLocaleString()}` : '-');

const winsLeftIn = (item) => {
    if (item.globalStock === null || item.globalStock === undefined) return null;
    const remaining = Math.max(0, item.globalStock - item.timesAwarded);
    return Math.floor(remaining / Math.max(1, item.prizeQuantity || 1));
};

const wheelsToExhaust = (item) => {
    const wins = winsLeftIn(item);
    if (wins === null || item.chance <= 0) return null;
    return wins <= 0 ? 0 : Math.round(wins / item.chance);
};

// weight the other segments each need for `rare` (at its current weight) to sit at `target`.
// target = rare / (rare + others * w)  ->  w = rare * (1 - target) / (others * target)
const weightForChance = (target, rareWeight, otherCount) => {
    if (otherCount <= 0 || target <= 0 || target >= 1) return null;
    return Math.max(1, Math.round((rareWeight * (1 - target)) / (otherCount * target)));
};

function OddsPanel({ items, totalWeight }) {
    const [open, setOpen] = useState(true);
    const [dailyPlayers, setDailyPlayers] = useState(60);

    const live = items.filter(isLive);
    const off = items.filter(item => !isLive(item));
    const perDay = Number(dailyPlayers) > 0 ? Number(dailyPlayers) : 0;

    const rarest = live.reduce((a, b) => (a === null || b.weight < a.weight ? b : a), null);
    const rest = rarest ? live.filter(item => item.id !== rarest.id) : [];

    const days = (wheels) => (perDay > 0 ? wheels / perDay : null);
    const daysText = (wheels) => {
        const d = days(wheels);
        if (d === null) return '';
        return d >= 1 ? `~${d < 10 ? d.toFixed(1) : fmt(Math.round(d))} days` : 'under a day';
    };

    return (
        <div className="border border-coal/15 rounded">
            <button
                onClick={() => setOpen(!open)}
                className="w-full flex items-center justify-between px-3 py-2 text-left"
            >
                <span className="text-sm font-semibold text-coal">How the odds work</span>
                <span className="text-xs text-coal/50">{open ? 'Hide' : 'Show'}</span>
            </button>

            {open && (
                <div className="px-3 pb-3 space-y-3">
                    <div className="text-xs text-coal/60">
                        The wheel lands on exactly one segment, and a segment's chance is its own weight
                        divided by the total live weight. Weights are relative rather than percentages, so
                        to make one prize rarer you raise the others instead of lowering it. Sold out,
                        switched off and zero weight segments are not on the wheel at all, and the rest
                        share their odds out between them.
                    </div>

                    {live.length === 0 && (
                        <div className="text-xs text-coal/50">Nothing is live on the wheel right now.</div>
                    )}

                    {live.length > 0 && (
                        <>
                            <div className="text-xs text-coal/70 border border-coal/10 rounded bg-coal/5 p-2 font-mono break-words">
                                {live.map(item => item.weight).join(' + ')} = {totalWeight}
                            </div>

                            <div className="flex h-4 w-full overflow-hidden rounded border border-coal/15">
                                {live.map(item => (
                                    <div
                                        key={item.id}
                                        style={{ width: `${item.chance * 100}%`, background: item.segmentColor }}
                                        title={`${item.label} - ${pct(item.chance)}`}
                                    />
                                ))}
                            </div>

                            <label className="text-xs text-coal/60 flex items-center gap-2">
                                Players a day
                                <input
                                    type="number"
                                    min="1"
                                    value={dailyPlayers}
                                    onChange={e => setDailyPlayers(e.target.value)}
                                    className="w-20 border border-coal/20 rounded px-2 py-1 text-sm text-coal"
                                />
                                <span className="text-coal/40">
                                    everyone gets one guaranteed wheel a day
                                </span>
                            </label>

                            <div className="overflow-x-auto">
                                <table className="w-full text-xs">
                                    <thead>
                                        <tr className="text-coal/50 text-left">
                                            <th className="font-normal py-1">Segment</th>
                                            <th className="font-normal py-1 text-right">Weight</th>
                                            <th className="font-normal py-1 text-right">Sum</th>
                                            <th className="font-normal py-1 text-right">Chance</th>
                                            <th className="font-normal py-1 text-right">Odds</th>
                                            <th className="font-normal py-1 text-right">One every</th>
                                            <th className="font-normal py-1 text-right">Stock lasts</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {live.map(item => {
                                            const wheels = wheelsToExhaust(item);
                                            return (
                                                <tr key={item.id} className="border-t border-coal/10">
                                                    <td className="py-1">
                                                        <span className="flex items-center gap-2">
                                                            <span
                                                                className="w-3 h-3 rounded-full border border-coal/20 shrink-0"
                                                                style={{ background: item.segmentColor }}
                                                            />
                                                            <span className="text-coal">{item.label}</span>
                                                        </span>
                                                    </td>
                                                    <td className="py-1 text-right text-coal/70">{item.weight}</td>
                                                    <td className="py-1 text-right text-coal/50 font-mono whitespace-nowrap">
                                                        {item.weight} / {totalWeight}
                                                    </td>
                                                    <td className="py-1 text-right text-coal">{pct(item.chance)}</td>
                                                    <td className="py-1 text-right text-coal/70 whitespace-nowrap">
                                                        {oneIn(item.chance)}
                                                    </td>
                                                    <td className="py-1 text-right text-coal/70 whitespace-nowrap">
                                                        {item.chance > 0 ? daysText(1 / item.chance) : '-'}
                                                    </td>
                                                    <td className="py-1 text-right text-coal/70 whitespace-nowrap">
                                                        {wheels === null
                                                            ? 'unlimited'
                                                            : `${fmt(wheels)} wheels, ${daysText(wheels)}`}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}

                    {rarest && rest.length > 0 && (
                        <div className="text-xs text-coal/60 border-t border-coal/10 pt-2 space-y-1">
                            <div>
                                Rarest live segment is <span className="text-coal">{rarest.label}</span>:{' '}
                                <span className="font-mono">{rarest.weight} / {totalWeight} = {pct(rarest.chance)}</span>,
                                so {oneIn(rarest.chance)} wheels.
                            </div>
                            <div>
                                To move it without touching its weight, set the other {rest.length}{' '}
                                segment{rest.length !== 1 ? 's' : ''} to:{' '}
                                {[0.02, 0.01, 0.005, 0.001]
                                    .map(target => {
                                        const w = weightForChance(target, rarest.weight, rest.length);
                                        return w === null ? null : `${pct(target)} = weight ${fmt(w)} each`;
                                    })
                                    .filter(Boolean)
                                    .join(', ')}.
                            </div>
                        </div>
                    )}

                    {off.length > 0 && (
                        <div className="text-xs text-coal/50 border-t border-coal/10 pt-2">
                            Not on the wheel:{' '}
                            {off.map(item => {
                                const why = !item.isActive ? 'switched off'
                                    : item.soldOut ? 'sold out'
                                        : 'weight 0, can never land';
                                return `${item.label} (${why})`;
                            }).join(', ')}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

const Stat = ({ label, value, tone }) => (
    <div className="flex min-w-[110px] flex-col border border-gray-200 bg-white px-3 py-2">
        <span className="font-['Jersey_10'] text-[13px] uppercase tracking-[1px] text-gray-500">{label}</span>
        <span className={`font-['Jersey_10'] text-[26px] leading-none ${tone || 'text-gray-900'}`}>{value}</span>
    </div>
);

function SlimeSpinsAdmin({ userData }) {
    const [tab, setTab] = useState('overview');
    const [overview, setOverview] = useState(null);
    const [settings, setSettings] = useState(null);
    const [items, setItems] = useState([]);
    const [totalWeight, setTotalWeight] = useState(0);
    const [gifts, setGifts] = useState([]);
    const [deliveries, setDeliveries] = useState([]);
    const [deliveryFilter, setDeliveryFilter] = useState('needed');
    const [log, setLog] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const [editing, setEditing] = useState(null);
    const catalogues = useCosmeticCatalogues();
    const [frankoSets, setFrankoSets] = useState([]);
    const [frankoFigures, setFrankoFigures] = useState([]);
    const [playerName, setPlayerName] = useState('');
    const [player, setPlayer] = useState(null);

    const [giftForm, setGiftForm] = useState({
        giftType: 'spins',
        target: 'users',
        habboNames: '',
        spins: 10,
        bonusScatters: 3,
        note: '',
    });

    const loadOverview = useCallback(async () => {
        const res = await getSlotAdminOverview();
        if (res?.success) setOverview(res);
    }, []);

    const loadItems = useCallback(async () => {
        const res = await getSlotWheelItems();
        if (res?.success) {
            setItems(res.items || []);
            setTotalWeight(res.totalWeight || 0);
        }
    }, []);

    const loadGifts = useCallback(async () => {
        const res = await getSlotGifts(50);
        if (res?.success) setGifts(res.gifts || []);
    }, []);

    const loadLog = useCallback(async () => {
        const res = await getSlotWheelLog(75);
        if (res?.success) setLog(res.log || []);
    }, []);

    const loadDeliveries = useCallback(async (state) => {
        const res = await getSlotWheelDeliveries(state === 'all' ? null : state);
        if (res?.success) setDeliveries(res.deliveries || []);
    }, []);

    const loadSettings = useCallback(async () => {
        const res = await getSlotAdminSettings();
        if (res?.success) setSettings(res.settings);
    }, []);

    useEffect(() => {
        (async () => {
            try {
                await Promise.all([
                    loadOverview(), loadItems(), loadGifts(), loadLog(), loadSettings(),
                    loadDeliveries(deliveryFilter),
                ]);
            } catch (e) {
                toast.error('Could not load Slimeburger Spin admin data.');
            } finally {
                setLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadOverview, loadItems, loadGifts, loadLog, loadSettings, loadDeliveries]);

    useEffect(() => {
        if (editing?.itemClass !== 'frankopop' || frankoSets.length) return;
        (async () => {
            try {
                const sets = await fetchAdminSets();
                setFrankoSets(sets || []);
            } catch {
                toast.error('Could not load FrankoPop sets.');
            }
        })();
    }, [editing, frankoSets.length]);

    const editingFrankoSetId = editing?.itemClass === 'frankopop' ? parseClassData(editing.classData).setId : null;

    useEffect(() => {
        if (!editingFrankoSetId) {
            setFrankoFigures([]);
            return;
        }
        (async () => {
            try {
                const res = await fetchAdminSet(editingFrankoSetId);
                setFrankoFigures(res?.figures || []);
            } catch {
                setFrankoFigures([]);
            }
        })();
    }, [editingFrankoSetId]);

    const handleDeliveryState = async (delivery, state) => {
        const res = await updateSlotWheelDelivery(delivery.id, { state, note: delivery.note || null });
        if (!res?.success) {
            toast.error(res?.message || 'Could not update that delivery.');
            return;
        }
        await Promise.all([loadDeliveries(deliveryFilter), loadOverview()]);
    };

    const changeDeliveryFilter = async (next) => {
        setDeliveryFilter(next);
        await loadDeliveries(next);
    };

    const handleSaveItem = async () => {
        if (!editing) return;
        setSaving(true);
        try {
            const payload = {
                ...editing,
                prizeQuantity: Number(editing.prizeQuantity) || 1,
                globalStock: editing.globalStock === '' ? null : Number(editing.globalStock),
            };
            const res = editing.id
                ? await updateSlotWheelItem(editing.id, payload)
                : await createSlotWheelItem(payload);
            if (!res?.success) {
                toast.error(res?.message || 'Could not save the segment.');
                return;
            }
            toast.success(editing.id ? 'Segment updated.' : 'Segment added.');
            setEditing(null);
            await loadItems();
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteItem = async (item) => {
        if (!window.confirm(`Remove "${item.label}" from the wheel? Past awards stay in the log.`)) return;
        const res = await deleteSlotWheelItem(item.id);
        if (!res?.success) {
            toast.error(res?.message || 'Could not remove the segment.');
            return;
        }
        toast.success('Segment removed.');
        await loadItems();
    };

    const handleGift = async () => {
        if (giftForm.target === 'users' && !giftForm.habboNames.trim()) {
            toast.error('Enter at least one Habbo name, or switch the target to everyone.');
            return;
        }
        if (giftForm.target === 'everyone'
            && !window.confirm('This gifts every registered account. Are you sure?')) return;

        setSaving(true);
        try {
            const res = await createSlotGift({
                ...giftForm,
                habboNames: giftForm.habboNames.split(',').map(n => n.trim()).filter(Boolean),
            });
            if (!res?.success) {
                toast.error(res?.message || 'Could not send the gift.');
                return;
            }
            toast.success(`Gifted ${res.granted} player${res.granted !== 1 ? 's' : ''}.`);
            if (res.missing?.length) toast.warn(`Not found: ${res.missing.join(', ')}`);
            setGiftForm(f => ({ ...f, habboNames: '', note: '' }));
            await Promise.all([loadGifts(), loadOverview()]);
        } finally {
            setSaving(false);
        }
    };

    const handleRevoke = async (gift) => {
        if (!window.confirm(`Revoke this gift from ${gift.habboName || 'this player'}?`)) return;
        const res = await revokeSlotGift(gift.id);
        if (!res?.success) {
            toast.error(res?.message || 'Could not revoke.');
            return;
        }
        await Promise.all([loadGifts(), loadOverview()]);
    };

    const handleGrantWheel = async () => {
        if (!playerName.trim()) return;
        const res = await grantSlotWheelSpin([playerName.trim()]);
        if (!res?.success) {
            toast.error(res?.message || 'Could not grant a wheel spin.');
            return;
        }
        toast.success(res.granted > 0 ? 'Wheel spin granted.' : 'That player already has a wheel waiting.');
    };

    const handleLookup = async () => {
        if (!playerName.trim()) return;
        const res = await getSlotAdminPlayer(playerName.trim());
        if (!res?.success) {
            toast.error(res?.message || 'Player not found.');
            setPlayer(null);
            return;
        }
        setPlayer(res);
    };

    const handleSaveSettings = async () => {
        setSaving(true);
        try {
            const res = await updateSlotAdminSettings(settings);
            if (!res?.success) {
                toast.error(res?.message || 'Could not save settings.');
                return;
            }
            setSettings(res.settings);
            toast.success('Settings saved.');
        } finally {
            setSaving(false);
        }
    };

    if (!userData || !userData.hasAdminRole) {
        return <Navigate to="/" replace />;
    }

    const setSetting = (key, value) => setSettings(s => ({ ...s, [key]: value }));

    const renderSegmentEditor = () => (
        <div className="border border-maroon/30 rounded p-3 space-y-2">
            <div className="text-sm font-semibold">
                {editing.id ? 'Edit segment' : 'New segment'}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <label className="text-xs text-coal/60">
                    Label
                    <input
                        value={editing.label}
                        onChange={e => setEditing({ ...editing, label: e.target.value })}
                        className="w-full border border-coal/20 rounded px-2 py-1 text-sm text-coal"
                    />
                </label>
                <label className="text-xs text-coal/60">
                    Type
                    <select
                        value={editing.itemClass}
                        onChange={e => setEditing({
                            ...editing,
                            itemClass: e.target.value,
                            classData: '',
                            image: '',
                            prizeQuantity: 1,
                        })}
                        className="w-full border border-coal/20 rounded px-2 py-1 text-sm text-coal"
                    >
                        {!ITEM_CLASSES.some(c => c.value === editing.itemClass) && (
                            <option value={editing.itemClass}>
                                {editing.itemClass
                                    ? `Unknown type "${editing.itemClass}"`
                                    : 'No type set'}
                            </option>
                        )}
                        {ITEM_CLASSES.map(c => (
                            <option key={c.value} value={c.value}>{c.label}</option>
                        ))}
                    </select>
                </label>
                <label className="text-xs text-coal/60 md:col-span-2">
                    Description
                    <input
                        value={editing.description}
                        onChange={e => setEditing({ ...editing, description: e.target.value })}
                        className="w-full border border-coal/20 rounded px-2 py-1 text-sm text-coal"
                    />
                </label>
                {COSMETIC_CLASSES[editing.itemClass] && (
                    <label className="text-xs text-coal/60 md:col-span-2">
                        {ITEM_CLASSES.find(c => c.value === editing.itemClass)?.label}
                        <CosmeticPicker
                            options={catalogues[editing.itemClass] || []}
                            value={parseClassData(editing.classData)[COSMETIC_CLASSES[editing.itemClass]] || ''}
                            onChange={(next) => setEditing(withClassData(editing, {
                                [COSMETIC_CLASSES[editing.itemClass]]: next,
                            }))}
                            onPick={(option) => {
                                if (!option) return;
                                setEditing(prev => ({
                                    ...withClassData(prev, {
                                        [COSMETIC_CLASSES[prev.itemClass]]: option.id,
                                    }),
                                    label: prev.label || option.label || option.id,
                                    image: option.shopImage || option.image || prev.image,
                                }));
                            }}
                            placeholder="id"
                        />
                    </label>
                )}
                {editing.itemClass === 'inventory_item' && (
                    <label className="text-xs text-coal/60 md:col-span-2">
                        Inventory item ID
                        <input
                            value={parseClassData(editing.classData).inventoryItemId || ''}
                            onChange={e => setEditing(withClassData(editing, { inventoryItemId: e.target.value }))}
                            className="w-full border border-coal/20 rounded px-2 py-1 text-sm font-mono text-coal"
                        />
                    </label>
                )}
                {editing.itemClass === 'furni' && (
                    <div className="text-xs text-coal/60 md:col-span-2">
                        Furni
                        <FurniPicker
                            value={parseClassData(editing.classData)}
                            onPick={(picked) => {
                                if (!picked) {
                                    setEditing({ ...editing, classData: '', image: '' });
                                    return;
                                }
                                setEditing(prev => ({
                                    ...withClassData(prev, {
                                        furniId: picked.furniId,
                                        furniCode: picked.furniCode,
                                        furniName: picked.furniName,
                                    }),
                                    label: prev.label || picked.furniName,
                                    image: picked.furniImage || prev.image,
                                }));
                            }}
                        />
                    </div>
                )}
                {editing.itemClass === 'manual' && (
                    <label className="text-xs text-coal/60 md:col-span-2">
                        Prize name (what staff have to hand over)
                        <input
                            value={parseClassData(editing.classData).prizeName || ''}
                            onChange={e => setEditing(withClassData(editing, { prizeName: e.target.value }))}
                            className="w-full border border-coal/20 rounded px-2 py-1 text-sm text-coal"
                        />
                    </label>
                )}
                {editing.itemClass === 'frankopop' && (
                    <>
                        <label className="text-xs text-coal/60 md:col-span-2">
                            FrankoPop set
                            <select
                                value={parseClassData(editing.classData).setId || ''}
                                onChange={e => setEditing(prev => ({
                                    ...withClassData(prev, { setId: e.target.value, figureId: null }),
                                    label: prev.label || frankoSets.find(s => s.id === e.target.value)?.name || prev.label,
                                }))}
                                className="w-full border border-coal/20 rounded px-2 py-1 text-sm text-coal"
                            >
                                <option value="">Pick a set...</option>
                                {frankoSets.map(s => (
                                    <option key={s.id} value={s.id}>{s.name}</option>
                                ))}
                            </select>
                        </label>
                        {editingFrankoSetId && (
                            <label className="text-xs text-coal/60 md:col-span-2">
                                Specific FrankoPop (optional)
                                <select
                                    value={parseClassData(editing.classData).figureId || ''}
                                    onChange={e => setEditing(withClassData(editing, { figureId: e.target.value || null }))}
                                    className="w-full border border-coal/20 rounded px-2 py-1 text-sm text-coal"
                                >
                                    <option value="">Random FrankoPop from this set</option>
                                    {frankoFigures.map(f => (
                                        <option key={f.id} value={f.id}>
                                            {f.name} ({f.rarityName}){f.ltd ? ' - LTD' : ''}
                                        </option>
                                    ))}
                                </select>
                                <span className="block mt-1 text-[11px] text-coal/40">
                                    Leave as random for a normal blind box. Pick one to always award that exact FrankoPop, like an admin gift.
                                </span>
                            </label>
                        )}
                    </>
                )}
                {MANUAL_CLASSES.includes(editing.itemClass) && (
                    <label className="text-xs text-coal/60">
                        {editing.itemClass === 'credits' ? 'Credits per win' : 'Amount handed out per win'}
                        <input
                            type="number"
                            min="1"
                            value={editing.prizeQuantity}
                            onChange={e => setEditing({ ...editing, prizeQuantity: e.target.value })}
                            className="w-full border border-coal/20 rounded px-2 py-1 text-sm text-coal"
                        />
                    </label>
                )}
                {editing.itemClass === 'framps' && (
                    <label className="text-xs text-coal/60">
                        Framps awarded
                        <input
                            type="number"
                            value={editing.frampAmount}
                            onChange={e => setEditing({ ...editing, frampAmount: e.target.value })}
                            className="w-full border border-coal/20 rounded px-2 py-1 text-sm text-coal"
                        />
                    </label>
                )}
                {!MANUAL_CLASSES.includes(editing.itemClass) && (
                <label className="text-xs text-coal/60">
                    Duplicate / blank pays (framps)
                    <input
                        type="number"
                        value={editing.fallbackStamps}
                        onChange={e => setEditing({ ...editing, fallbackStamps: e.target.value })}
                        className="w-full border border-coal/20 rounded px-2 py-1 text-sm text-coal"
                    />
                </label>
                )}
                <label className="text-xs text-coal/60">
                    Weight
                    <input
                        type="number"
                        value={editing.weight}
                        onChange={e => setEditing({ ...editing, weight: e.target.value })}
                        className="w-full border border-coal/20 rounded px-2 py-1 text-sm text-coal"
                    />
                </label>
                <label className="text-xs text-coal/60">
                    Segment colour
                    <div className="flex items-center gap-2">
                        <input
                            type="color"
                            value={editing.segmentColor}
                            onChange={e => setEditing({ ...editing, segmentColor: e.target.value })}
                            className="flex-1 min-w-0 border border-coal/20 rounded px-2 py-1 h-8"
                        />
                        <button
                            type="button"
                            onClick={() => setEditing({
                                ...editing,
                                segmentColor: nextSegmentColour(items.filter(i => i.id !== editing.id)),
                            })}
                            className="px-2 py-1 rounded bg-coal/10 hover:bg-coal/20 text-xs shrink-0"
                        >
                            Auto
                        </button>
                    </div>
                </label>
                <label className="text-xs text-coal/60">
                    {MANUAL_CLASSES.includes(editing.itemClass)
                        ? 'Total quantity (blank = unlimited)'
                        : 'Global stock (blank = unlimited)'}
                    <input
                        value={editing.globalStock}
                        onChange={e => setEditing({ ...editing, globalStock: e.target.value })}
                        className="w-full border border-coal/20 rounded px-2 py-1 text-sm text-coal"
                    />
                </label>
                <label className="text-xs text-coal/60 md:col-span-2">
                    Segment image URL (shown on the wheel)
                    <div className="flex items-center gap-2">
                        <div className="h-[34px] w-[34px] shrink-0 rounded border border-coal/20 bg-coal/5 flex items-center justify-center overflow-hidden">
                            {editing.image
                                ? <img src={editing.image} alt="" className="max-h-full max-w-full object-contain" />
                                : null}
                        </div>
                        <input
                            value={editing.image}
                            onChange={e => setEditing({ ...editing, image: e.target.value })}
                            className="flex-1 min-w-0 border border-coal/20 rounded px-2 py-1 text-sm font-mono text-coal"
                        />
                    </div>
                </label>
                <label className="text-xs text-coal/60">
                    Sort order
                    <input
                        type="number"
                        value={editing.sortOrder}
                        onChange={e => setEditing({ ...editing, sortOrder: e.target.value })}
                        className="w-full border border-coal/20 rounded px-2 py-1 text-sm text-coal"
                    />
                </label>
                <label className="text-xs text-coal/60 flex items-center gap-2 mt-4">
                    <input
                        type="checkbox"
                        checked={editing.isActive}
                        onChange={e => setEditing({ ...editing, isActive: e.target.checked })}
                    />
                    On the wheel
                </label>
            </div>
            <div className="flex gap-2">
                <button
                    onClick={handleSaveItem}
                    disabled={saving}
                    className="px-3 py-1 rounded bg-maroon text-white text-sm disabled:opacity-50"
                >
                    {saving ? 'Saving...' : 'Save'}
                </button>
                <button
                    onClick={() => setEditing(null)}
                    className="px-3 py-1 rounded bg-coal/10 hover:bg-coal/20 text-sm"
                >
                    Cancel
                </button>
            </div>
        </div>
    );

    const deskTabs = TABS.map(t => (
        t.key === 'deliveries'
            ? { ...t, badge: overview?.wheel?.deliveriesNeeded || 0 }
            : t
    ));

    return (
        <SlimeSpinsDesk tabs={deskTabs} active={tab} onSelect={setTab}>
            <DeskRibbon>
                <DeskTitle>Slimeburger Spin</DeskTitle>
                {settings && (
                    <span className={`text-[13px] uppercase tracking-[1px] ${
                        settings.game_enabled === '1' ? 'text-green-700' : 'text-red-600'
                    }`}>
                        {settings.game_enabled === '1' ? 'Open' : 'Closed'}
                    </span>
                )}
                {overview?.wheel?.deliveriesNeeded > 0 && (
                    <DeskNote>
                        {fmt(overview.wheel.deliveriesNeeded)} prize
                        {overview.wheel.deliveriesNeeded !== 1 ? 's' : ''} waiting to be delivered
                    </DeskNote>
                )}
            </DeskRibbon>

            <DeskPanel>
                <div>
                    {loading && <div className="text-coal/60 text-sm">Loading...</div>}

                    {!loading && tab === 'overview' && overview && (
                        <div className="space-y-4">
                            <div>
                                <div className="text-xs uppercase tracking-wider text-coal/50 mb-2">All time</div>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                    <Stat label="Spins" value={fmt(overview.overview.spins)} />
                                    <Stat label="Daily / Stakes" value={`${fmt(overview.overview.dailySpins)} / ${fmt(overview.overview.stakesSpins)}`} />
                                    <Stat label="Staked" value={fmt(overview.overview.staked)} />
                                    <Stat label="Paid out" value={fmt(overview.overview.won)} />
                                    <Stat label="Actual RTP" value={pct(overview.overview.rtp)} />
                                    <Stat label="Gifted spins used" value={fmt(overview.overview.giftedSpins)} />
                                    <Stat label="Wheels opened" value={fmt(overview.overview.wheelTriggers)} />
                                    <Stat label="Near misses" value={fmt(overview.overview.wheelTeases)} />
                                </div>
                            </div>

                            <div>
                                <div className="text-xs uppercase tracking-wider text-coal/50 mb-2">Today</div>
                                <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                                    <Stat label="Spins" value={fmt(overview.today.spins)} />
                                    <Stat label="Players" value={fmt(overview.today.players)} />
                                    <Stat label="Staked" value={fmt(overview.today.staked)} />
                                    <Stat label="Paid out" value={fmt(overview.today.won)} />
                                    <Stat label="Wheels" value={fmt(overview.today.wheelTriggers)} />
                                </div>
                            </div>

                            <div>
                                <div className="text-xs uppercase tracking-wider text-coal/50 mb-2">Wheel</div>
                                <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                                    <Stat label="Spun" value={fmt(overview.wheel.spins)} />
                                    <Stat label="Items won" value={fmt(overview.wheel.items)} />
                                    <Stat label="Duplicates" value={fmt(overview.wheel.duplicates)} tone="text-orange-600" />
                                    <Stat label="Framps paid" value={fmt(overview.wheel.frampsPaid)} />
                                    <Stat label="Awaiting spin" value={fmt(overview.wheel.pending)} />
                                    <Stat
                                        label="Deliveries needed"
                                        value={fmt(overview.wheel.deliveriesNeeded)}
                                        tone={overview.wheel.deliveriesNeeded > 0 ? 'text-red-600' : undefined}
                                    />
                                </div>
                            </div>

                            <div>
                                <div className="text-xs uppercase tracking-wider text-coal/50 mb-2">Outstanding gifts</div>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                    <Stat label="Spin gifts active" value={fmt(overview.gifts.activeSpinGifts)} />
                                    <Stat label="Free spins owed" value={fmt(overview.gifts.spinsOutstanding)} />
                                    <Stat label="Bonus rounds owed" value={fmt(overview.gifts.activeBonusGifts)} />
                                </div>
                            </div>

                            <div>
                                <div className="text-xs uppercase tracking-wider text-coal/50 mb-2">Recent jackpots</div>
                                {overview.jackpots.length === 0 && <div className="text-coal/50 text-sm">None yet.</div>}
                                {overview.jackpots.map((j, i) => (
                                    <div key={i} className="text-sm border-b border-coal/10 py-1">
                                        {j.habboName || j.userId} won {fmt(j.win)} framps - {when(j.at)}
                                    </div>
                                ))}
                            </div>

                            <div className="border-t border-coal/10 pt-3">
                                <div className="text-xs uppercase tracking-wider text-coal/50 mb-2">Player lookup</div>
                                <div className="flex flex-wrap gap-2 mb-2">
                                    <input
                                        value={playerName}
                                        onChange={e => setPlayerName(e.target.value)}
                                        placeholder="Habbo name"
                                        className="border border-coal/20 rounded px-2 py-1 text-sm"
                                    />
                                    <button onClick={handleLookup} className="px-3 py-1 rounded bg-coal/10 hover:bg-coal/20 text-sm">
                                        Look up
                                    </button>
                                    <button onClick={handleGrantWheel} className="px-3 py-1 rounded bg-pink-100 hover:bg-pink-200 text-sm">
                                        Give a wheel spin
                                    </button>
                                </div>
                                {player && (
                                    <div className="text-sm space-y-1">
                                        <div className="font-semibold">{player.player.habboName}</div>
                                        <div>
                                            {fmt(player.stats.spins)} spins, staked {fmt(player.stats.staked)}, won {fmt(player.stats.won)},
                                            {' '}{fmt(player.stats.wheelTriggers)} wheels. Last spin {when(player.stats.lastSpin)}.
                                        </div>
                                        {player.gifts.length > 0 && (
                                            <div className="text-coal/70">
                                                Gifts: {player.gifts.map(g => `${g.giftType === 'spins' ? `${g.spinsRemaining} spins` : `bonus x${g.bonusScatters}`} (${g.state})`).join(', ')}
                                            </div>
                                        )}
                                        {player.wheelSpins.length > 0 && (
                                            <div className="text-coal/70">
                                                Wheel: {player.wheelSpins.map(w => `${w.label || 'nothing'}${w.framps ? ` +${w.framps}` : ''}`).join(', ')}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {!loading && tab === 'wheel' && (
                        <div className="space-y-3">
                            <div className="flex items-center justify-between">
                                <div className="text-sm text-coal/60">
                                    {items.length} segment{items.length !== 1 ? 's' : ''}, total live weight {totalWeight}.
                                </div>
                                <button
                                    onClick={() => setEditing({ ...BLANK_ITEM, segmentColor: nextSegmentColour(items) })}
                                    className="px-3 py-1 rounded bg-maroon text-white text-sm"
                                >
                                    Add segment
                                </button>
                            </div>

                            <OddsPanel items={items} totalWeight={totalWeight} />

                            {items.length === 0 && (
                                <div className="text-coal/50 text-sm border border-dashed border-coal/20 rounded p-4">
                                    The wheel is empty. Players who trigger it will be told there are no prizes, so add at
                                    least one segment before turning the wheel on.
                                </div>
                            )}

                            <div className="space-y-2">
                                {editing && !editing.id && renderSegmentEditor()}
                                {items.map(item => (
                                    <React.Fragment key={item.id}>
                                    <div className="border border-coal/10 rounded p-2 flex flex-wrap items-center gap-3">
                                        <div
                                            className="w-4 h-4 rounded-full border border-coal/20 shrink-0"
                                            style={{ background: item.segmentColor }}
                                        />
                                        <div className="flex-1 min-w-[180px]">
                                            <div className={`text-sm ${item.isActive ? 'text-coal' : 'text-coal/40 line-through'}`}>
                                                {item.label}
                                                {item.soldOut && <span className="ml-2 text-xs text-red-600">SOLD OUT</span>}
                                            </div>
                                            <div className="text-xs text-coal/50">
                                                {item.itemClass}
                                                {item.itemClass === 'framps' ? ` - ${item.frampAmount} framps` : ''}
                                                {item.describes ? ` - ${item.describes}` : ''}
                                                {item.manual ? ` - ${item.prizeQuantity} per win, delivered by staff` : ''}
                                                {item.itemClass === 'frankopop'
                                                    ? ` - falls back to ${item.fallbackStamps || 'default'} framps if it can't be awarded`
                                                    : ''}
                                                {!item.manual && item.itemClass !== 'framps' && item.itemClass !== 'nothing' && item.itemClass !== 'frankopop'
                                                    ? ` - duplicate pays ${item.fallbackStamps || 'default'}`
                                                    : ''}
                                            </div>
                                        </div>
                                        <div className="text-xs text-coal/60 text-right">
                                            <div>weight {item.weight} ({pct(item.chance)})</div>
                                            <div>
                                                won {item.awardedItems}
                                                {item.awardedDuplicates > 0 ? ` / ${item.awardedDuplicates} dupes` : ''}
                                                {item.globalStock !== null ? ` / stock ${item.timesAwarded}of${item.globalStock}` : ''}
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => setEditing({
                                                id: item.id,
                                                label: item.label,
                                                description: item.description || '',
                                                itemClass: item.itemClass,
                                                classData: item.classData || '',
                                                prizeQuantity: item.prizeQuantity || 1,
                                                frampAmount: item.frampAmount,
                                                fallbackStamps: item.fallbackStamps,
                                                weight: item.weight,
                                                segmentColor: item.segmentColor,
                                                image: item.image || '',
                                                globalStock: item.globalStock === null ? '' : item.globalStock,
                                                isActive: item.isActive,
                                                sortOrder: item.sortOrder,
                                            })}
                                            className="px-2 py-1 rounded bg-coal/10 hover:bg-coal/20 text-xs"
                                        >
                                            Edit
                                        </button>
                                        <button
                                            onClick={() => handleDeleteItem(item)}
                                            className="px-2 py-1 rounded bg-red-100 hover:bg-red-200 text-xs text-red-800"
                                        >
                                            Remove
                                        </button>
                                    </div>
                                    {editing && editing.id === item.id && renderSegmentEditor()}
                                    </React.Fragment>
                                ))}
                            </div>

                        </div>
                    )}

                    {!loading && tab === 'deliveries' && (
                        <div className="space-y-3">
                            <div className="flex items-center justify-between flex-wrap gap-2">
                                <div className="text-sm text-coal/60">
                                    Prizes the wheel handed out that a member of staff has to deliver in the hotel.
                                </div>
                                <div className="flex gap-1">
                                    {['needed', 'completed', 'cancelled', 'all'].map(state => (
                                        <button
                                            key={state}
                                            onClick={() => changeDeliveryFilter(state)}
                                            className={`px-2 py-1 rounded text-xs capitalize ${
                                                deliveryFilter === state ? 'bg-maroon text-white' : 'bg-coal/10 hover:bg-coal/20'
                                            }`}
                                        >
                                            {state}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {deliveries.length === 0 && (
                                <div className="text-coal/50 text-sm border border-dashed border-coal/20 rounded p-4">
                                    Nothing here.
                                </div>
                            )}

                            <div className="space-y-2">
                                {deliveries.map(d => (
                                    <div key={d.id} className="border border-coal/10 rounded p-2 flex flex-wrap items-center gap-3">
                                        <div className="h-9 w-9 shrink-0 flex items-center justify-center">
                                            {d.image
                                                ? <img src={d.image} alt="" className="max-h-full max-w-full object-contain" />
                                                : <span className="text-[10px] text-coal/40 uppercase">{DELIVERY_KINDS[d.kind] || d.kind}</span>}
                                        </div>
                                        <div className="flex-1 min-w-[200px]">
                                            <div className="text-sm text-coal">
                                                <span className="font-semibold">{d.habboName || d.userId}</span>
                                                {' won '}
                                                {d.quantity > 1 ? `${d.quantity}x ` : ''}
                                                {d.label}
                                            </div>
                                            <div className="text-xs text-coal/50">
                                                {DELIVERY_KINDS[d.kind] || d.kind}
                                                {d.reference ? ` - ${d.reference}` : ''}
                                                {' - '}{when(d.at)}
                                                {d.handledByName ? ` - ${d.state} by ${d.handledByName}` : ''}
                                            </div>
                                        </div>
                                        <label className="text-xs text-coal/60 flex items-center gap-2">
                                            <input
                                                type="checkbox"
                                                checked={d.state === 'completed'}
                                                onChange={e => handleDeliveryState(d, e.target.checked ? 'completed' : 'needed')}
                                            />
                                            Delivered
                                        </label>
                                        {d.state !== 'cancelled' && (
                                            <button
                                                onClick={() => handleDeliveryState(d, 'cancelled')}
                                                className="px-2 py-1 rounded bg-red-100 hover:bg-red-200 text-xs text-red-800"
                                            >
                                                Cancel
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {!loading && tab === 'gifts' && (
                        <div className="space-y-4">
                            <div className="border border-coal/10 rounded p-3 space-y-2">
                                <div className="text-sm font-semibold">Send a gift</div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                    <label className="text-xs text-coal/60">
                                        Gift
                                        <select
                                            value={giftForm.giftType}
                                            onChange={e => setGiftForm({ ...giftForm, giftType: e.target.value })}
                                            className="w-full border border-coal/20 rounded px-2 py-1 text-sm text-coal"
                                        >
                                            <option value="spins">Free Framp Stakes spins</option>
                                            <option value="bonus">Free bonus round</option>
                                        </select>
                                    </label>
                                    <label className="text-xs text-coal/60">
                                        Target
                                        <select
                                            value={giftForm.target}
                                            onChange={e => setGiftForm({ ...giftForm, target: e.target.value })}
                                            className="w-full border border-coal/20 rounded px-2 py-1 text-sm text-coal"
                                        >
                                            <option value="users">Named players</option>
                                            <option value="everyone">Everyone</option>
                                        </select>
                                    </label>
                                    {giftForm.target === 'users' && (
                                        <label className="text-xs text-coal/60 md:col-span-2">
                                            Habbo names (comma separated)
                                            <input
                                                value={giftForm.habboNames}
                                                onChange={e => setGiftForm({ ...giftForm, habboNames: e.target.value })}
                                                className="w-full border border-coal/20 rounded px-2 py-1 text-sm text-coal"
                                            />
                                        </label>
                                    )}
                                    {giftForm.giftType === 'spins' && (
                                        <label className="text-xs text-coal/60">
                                            Spins each
                                            <input
                                                type="number"
                                                value={giftForm.spins}
                                                onChange={e => setGiftForm({ ...giftForm, spins: e.target.value })}
                                                className="w-full border border-coal/20 rounded px-2 py-1 text-sm text-coal"
                                            />
                                        </label>
                                    )}
                                    {giftForm.giftType === 'bonus' && (
                                        <label className="text-xs text-coal/60">
                                            Bonus symbols (3-6, 6 is Slime Overload)
                                            <select
                                                value={giftForm.bonusScatters}
                                                onChange={e => setGiftForm({ ...giftForm, bonusScatters: Number(e.target.value) })}
                                                className="w-full border border-coal/20 rounded px-2 py-1 text-sm text-coal"
                                            >
                                                <option value={3}>3 - 15 free spins</option>
                                                <option value={4}>4 - 20 free spins</option>
                                                <option value={5}>5 - 50 free spins</option>
                                                <option value={6}>6 - Slime Overload</option>
                                            </select>
                                        </label>
                                    )}
                                    <label className="text-xs text-coal/60 md:col-span-2">
                                        Note shown to the player (optional)
                                        <input
                                            value={giftForm.note}
                                            onChange={e => setGiftForm({ ...giftForm, note: e.target.value })}
                                            className="w-full border border-coal/20 rounded px-2 py-1 text-sm text-coal"
                                        />
                                    </label>
                                </div>
                                <button
                                    onClick={handleGift}
                                    disabled={saving}
                                    className="px-3 py-1 rounded bg-maroon text-white text-sm disabled:opacity-50"
                                >
                                    {saving ? 'Sending...' : 'Send gift'}
                                </button>
                            </div>

                            <div>
                                <div className="text-xs uppercase tracking-wider text-coal/50 mb-2">Recent gifts</div>
                                {gifts.length === 0 && <div className="text-coal/50 text-sm">Nothing gifted yet.</div>}
                                {gifts.map(g => (
                                    <div key={g.id} className="border-b border-coal/10 py-1 text-sm flex items-center gap-2">
                                        <div className="flex-1">
                                            <span className="font-semibold">{g.habboName || g.userId}</span>
                                            {' - '}
                                            {g.giftType === 'spins'
                                                ? `${g.spinsRemaining}/${g.spinsGranted} spins left`
                                                : `bonus round (${g.bonusScatters} symbols)`}
                                            {' - '}
                                            <span className={g.state === 'active' ? 'text-green-700' : 'text-coal/50'}>{g.state}</span>
                                            <span className="text-coal/40"> - {when(g.at)}{g.grantedByName ? ` by ${g.grantedByName}` : ''}</span>
                                        </div>
                                        {g.state === 'active' && (
                                            <button
                                                onClick={() => handleRevoke(g)}
                                                className="px-2 py-1 rounded bg-red-100 hover:bg-red-200 text-xs text-red-800"
                                            >
                                                Revoke
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {!loading && tab === 'settings' && settings && (
                        <div className="space-y-3 max-w-xl">
                            <label className="flex items-center gap-2 text-sm">
                                <input
                                    type="checkbox"
                                    checked={settings.game_enabled === '1'}
                                    onChange={e => setSetting('game_enabled', e.target.checked ? '1' : '0')}
                                />
                                Machine open
                            </label>
                            <label className="text-xs text-coal/60 block">
                                Closed message
                                <input
                                    value={settings.game_closed_message || ''}
                                    onChange={e => setSetting('game_closed_message', e.target.value)}
                                    className="w-full border border-coal/20 rounded px-2 py-1 text-sm text-coal"
                                />
                            </label>
                            <label className="flex items-center gap-2 text-sm">
                                <input
                                    type="checkbox"
                                    checked={settings.wheel_enabled === '1'}
                                    onChange={e => setSetting('wheel_enabled', e.target.checked ? '1' : '0')}
                                />
                                Slime Wheel enabled
                            </label>
                            <label className="flex items-center gap-2 text-sm">
                                <input
                                    type="checkbox"
                                    checked={settings.wheel_daily_pity === '1'}
                                    onChange={e => setSetting('wheel_daily_pity', e.target.checked ? '1' : '0')}
                                />
                                Guarantee one wheel per player per day
                            </label>
                            <label className="text-xs text-coal/60 block">
                                Earliest daily spin the guarantee may land on
                                <input
                                    type="number"
                                    value={settings.wheel_min_spin || ''}
                                    onChange={e => setSetting('wheel_min_spin', e.target.value)}
                                    className="w-full border border-coal/20 rounded px-2 py-1 text-sm text-coal"
                                />
                            </label>
                            <label className="flex items-center gap-2 text-sm">
                                <input
                                    type="checkbox"
                                    checked={settings.wheel_stakes_enabled === '1'}
                                    onChange={e => setSetting('wheel_stakes_enabled', e.target.checked ? '1' : '0')}
                                />
                                Wheels can land in Framp Stakes
                            </label>
                            <label className="text-xs text-coal/60 block">
                                Framp Stakes tease odds (1 in N spins shows a single wheel)
                                <input
                                    type="number"
                                    value={settings.wheel_stakes_tease_odds || ''}
                                    onChange={e => setSetting('wheel_stakes_tease_odds', e.target.value)}
                                    className="w-full border border-coal/20 rounded px-2 py-1 text-sm text-coal"
                                />
                            </label>
                            <label className="text-xs text-coal/60 block">
                                Framp Stakes win odds (1 in N spins triggers the wheel)
                                <input
                                    type="number"
                                    value={settings.wheel_stakes_win_odds || ''}
                                    onChange={e => setSetting('wheel_stakes_win_odds', e.target.value)}
                                    className="w-full border border-coal/20 rounded px-2 py-1 text-sm text-coal"
                                />
                            </label>
                            <label className="text-xs text-coal/60 block">
                                Default duplicate payout (framps)
                                <input
                                    type="number"
                                    value={settings.wheel_duplicate_stamps || ''}
                                    onChange={e => setSetting('wheel_duplicate_stamps', e.target.value)}
                                    className="w-full border border-coal/20 rounded px-2 py-1 text-sm text-coal"
                                />
                            </label>
                            <button
                                onClick={handleSaveSettings}
                                disabled={saving}
                                className="px-3 py-1 rounded bg-maroon text-white text-sm disabled:opacity-50"
                            >
                                {saving ? 'Saving...' : 'Save settings'}
                            </button>
                        </div>
                    )}

                    {!loading && tab === 'log' && (
                        <div>
                            {log.length === 0 && <div className="text-coal/50 text-sm">No wheel spins yet.</div>}
                            {log.map(entry => (
                                <div key={entry.id} className="border-b border-coal/10 py-1 text-sm">
                                    <span className="font-semibold">{entry.habboName || entry.userId}</span>
                                    {' - '}
                                    {entry.state === 'pending'
                                        ? <span className="text-orange-600">waiting to spin</span>
                                        : <>{entry.label || 'nothing'} ({entry.outcome}){entry.framps > 0 ? ` +${entry.framps} framps` : ''}</>}
                                    <span className="text-coal/40"> - {entry.source} - {when(entry.at)}</span>
                                    {entry.detail && <div className="text-xs text-coal/50">{entry.detail}</div>}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </DeskPanel>
        </SlimeSpinsDesk>
    );
}

export default SlimeSpinsAdmin;
